import { useCallback, useEffect } from 'react';
import type { Movie, Vote } from '@filmsloth/shared';
import { ConfirmButton } from '../components/ConfirmButton.js';
import { MovieCard } from '../components/MovieCard.js';
import { ParticipantList } from '../components/ParticipantList.js';
import { RoundTimer } from '../components/RoundTimer.js';
import { SuggestFilmModal } from '../components/SuggestFilmModal.js';
import { SlothVoteNo, SlothVoteNeutral, SlothVoteYes } from '../components/Sloth.js';
import { useT } from '../i18n/index.js';
import { dragProgress } from '../lib/swipeGesture.js';
import { useReducedMotion } from '../lib/useReducedMotion.js';
import { usePinnedCard } from '../lib/usePinnedCard.js';
import { useSwipeable } from '../lib/useSwipeable.js';
import { useAccount } from '../session/useAccount.js';
import { useSession } from '../session/useSession.js';

interface SwipeScreenProps {
  onShowResults: () => void;
}

/** Stable empty queue, so an absent personal state is not a new array each render. */
const EMPTY_QUEUE: Movie[] = [];

/** The voting screen: one card at a time, three ways to answer. */
export function SwipeScreen({ onShowResults }: SwipeScreenProps) {
  const t = useT();
  const {
    state,
    personal,
    isHost,
    vote,
    markSeen,
    deferCard,
    undoVote,
    leave,
    finish,
    startRunoff,
    connection,
    pendingVoteCount,
    credentials,
    removeParticipant,
    transferHost,
  } = useSession();
  const { account } = useAccount();

  // Pinned, not simply `queue[0]`: somebody else's yes reorders the queue
  // underneath us, and the card in your hand has to stay put until you have
  // swiped it.
  const current = usePinnedCard(personal?.queue ?? EMPTY_QUEUE);
  const canUndo = personal?.canUndo ?? false;

  const castVote = useCallback(
    (choice: Vote) => {
      if (current) void vote(current.id, choice);
    },
    [current, vote],
  );

  const { swipe, deckRef, handlers } = useSwipeable(castVote, Boolean(current));

  // The card's tilt is the one piece of motion the stylesheet cannot reach:
  // it is written into an inline transform on every pointer move. Somebody
  // who asked their system for less movement gets a card that follows the
  // finger without rotating — the gesture still works, it just stops
  // swinging.
  const reducedMotion = useReducedMotion();
  const tiltDegrees = reducedMotion ? 0 : swipe.dx / 24;

  // Keyboard shortcuts mirror the three buttons, in the same left-to-right order.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Never steal a keystroke from someone filling in a form.
      const target = event.target;
      if (target instanceof HTMLElement && target.closest('input, textarea, select')) return;

      if (event.key === 'Backspace' || (event.key.toLowerCase() === 'z' && event.metaKey)) {
        if (!canUndo) return;
        event.preventDefault();
        void undoVote();
        return;
      }

      if (!current) return;
      // Up is not an answer, so it is the one arrow free for "not yet".
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        void deferCard(current.id);
        return;
      }
      const mapping: Record<string, Vote> = {
        ArrowLeft: 'dislike',
        ArrowDown: 'neutral',
        ArrowRight: 'like',
      };
      const choice = mapping[event.key];
      if (!choice) return;
      event.preventDefault();
      void vote(current.id, choice);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [current, vote, undoVote, deferCard, canUndo]);

  if (!state || !personal || !credentials) return null;

  const total = personal.votedCount + personal.remaining;
  const progress = total > 0 ? (personal.votedCount / total) * 100 : 0;

  return (
    <div className="screen swipe">
      {/* One compact bar instead of a row of full-size buttons: on a phone
          every pixel above the card is a pixel the vote buttons lose. */}
      <div className="swipe__top">
        <div className="swipe__bar">
          <span className="badge">{state.code}</span>
          <span className="spacer" />
          <button type="button" className="btn btn--ghost btn--small" onClick={onShowResults}>
            {t('swipe.results')}
          </button>
          {isHost ? (
            <ConfirmButton
              className="btn btn--ghost btn--small"
              label={t('swipe.finishSession')}
              confirmLabel={t('swipe.finishConfirm')}
              onConfirm={() => void finish()}
            />
          ) : (
            <ConfirmButton
              className="btn btn--ghost btn--small"
              label={t('swipe.leaveShort')}
              confirmLabel={t('swipe.leaveConfirm')}
              onConfirm={() => void leave()}
            />
          )}
        </div>
        <div className="swipe__progress">
          <div className="progress" aria-hidden="true">
            <div className="progress__bar" style={{ width: `${progress}%` }} />
          </div>
          <span className="muted small swipe__count">
            {t('swipe.progress', { voted: personal.votedCount, total })}
          </span>
        </div>
      </div>

      {connection === 'lost' ? (
        <p className="banner banner--warn">{t('error.connectionLost')}</p>
      ) : null}

      {/* Votes that were cast but have not reached anybody yet. Worth saying
          out loud: the cards left the screen, so without this the deck looks
          exactly as it does when everything went through. */}
      {pendingVoteCount > 0 ? (
        <p className="banner banner--warn" role="status">
          {t('swipe.pendingVotes', { count: pendingVoteCount })}
        </p>
      ) : null}

      <RoundTimer />

      {/* Who is where. Mostly for the moment a match seems overdue: the
          answer is nearly always one person who stopped swiping, and this is
          where the host can see that and do something about it. */}
      <details className="group-panel">
        <summary className="small muted">
          {t('group.title', {
            count: state.participants.filter((participant) => !participant.left).length,
          })}
        </summary>
        <div className="stack stack--tight">
          <div className="panel panel--flush">
            <ParticipantList
              participants={state.participants}
              meId={credentials.participantId}
              progress={state.progress}
              hostActions={
                isHost
                  ? {
                      onRemove: (id) => void removeParticipant(id),
                      onMakeHost: (id) => void transferHost(id),
                    }
                  : undefined
              }
            />
          </div>
          {isHost ? <p className="muted small">{t('group.stuckHint')}</p> : null}
        </div>
      </details>

      {current ? (
        <>
          <div
            ref={deckRef}
            className={`deck${swipe.dragging ? ' deck--dragging' : ''}`}
            style={{
              transform: `translate(${swipe.dx}px, ${swipe.dy}px) rotate(${tiltDegrees}deg)`,
            }}
            {...handlers}
          >
            <MovieCard movie={current} country={state.filters.country} />
            {swipe.intent ? (
              <span
                className={`swipe-hint swipe-hint--${swipe.intent}`}
                style={{ opacity: dragProgress(swipe) }}
                aria-hidden="true"
              >
                {swipe.intent === 'like'
                  ? t('swipe.like')
                  : swipe.intent === 'dislike'
                    ? t('swipe.dislike')
                    : t('swipe.neutral')}
              </span>
            ) : null}
          </div>

          {/* Docked to the bottom of the screen: however long the card gets —
              a trailer, a long synopsis, a small phone — the three answers
              are always one thumb away without scrolling. */}
          <div className="vote-bar vote-bar--docked">
            <button
              type="button"
              className="vote-btn vote-btn--dislike"
              onClick={() => void vote(current.id, 'dislike')}
            >
              <span className="vote-btn__icon" aria-hidden="true">
                <SlothVoteNo />
              </span>
              {t('swipe.dislike')}
            </button>
            <button
              type="button"
              className="vote-btn vote-btn--neutral"
              onClick={() => void vote(current.id, 'neutral')}
            >
              <span className="vote-btn__icon" aria-hidden="true">
                <SlothVoteNeutral />
              </span>
              {t('swipe.neutral')}
            </button>
            <button
              type="button"
              className="vote-btn vote-btn--like"
              onClick={() => void vote(current.id, 'like')}
            >
              <span className="vote-btn__icon" aria-hidden="true">
                <SlothVoteYes />
              </span>
              {t('swipe.like')}
            </button>
          </div>
          <div className="row row--center swipe__secondary">
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={() => void undoVote()}
              disabled={!canUndo}
            >
              ↶ {t('swipe.undo')}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={() => void deferCard(current.id)}
            >
              {t('swipe.later')}
            </button>
            {account ? (
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => void markSeen(current.id)}
              >
                {t('swipe.alreadySeen')}
              </button>
            ) : null}
            <SuggestFilmModal />
          </div>
          {/* Only where there is a keyboard to press: on a phone the tip
              names keys that do not exist. */}
          <p className="muted small center swipe__keyboard-hint">{t('swipe.keyboardHint')}</p>
        </>
      ) : (
        <div className="panel stack center">
          <h2>{t('swipe.emptyTitle')}</h2>
          <p className="muted">{t('swipe.emptyBody')}</p>
          {/* The deck is spent and nothing matched. Rather than leaving the
              group with a ranking and no decision, the host can put the best
              of it to a shortlist vote. Offered, never forced: loosening a
              filter and swiping on is still a valid answer. */}
          {state?.canStartRunoff ? (
            <div className="stack">
              <p className="muted small">{t('runoff.offer')}</p>
              {isHost ? (
                <button
                  type="button"
                  className="btn btn--primary btn--block"
                  onClick={() => void startRunoff()}
                >
                  {t('runoff.start')}
                </button>
              ) : (
                <p className="muted small center">{t('runoff.offerWaiting')}</p>
              )}
            </div>
          ) : null}
          <button type="button" className="btn btn--block" onClick={onShowResults}>
            {t('swipe.results')}
          </button>
          {/* An empty deck is exactly when a title somebody has in mind is
              worth adding. */}
          <SuggestFilmModal />
          {canUndo ? (
            <button
              type="button"
              className="btn btn--ghost btn--block"
              onClick={() => void undoVote()}
            >
              ↶ {t('swipe.undo')}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
